import { supabase } from "@/lib/supabase";

/**
 * Set a thread as publicly shareable
 *
 * @param {string} threadId - The ID of the thread to share
 * @param {boolean} isShared - Whether the thread should be shared (true) or private (false)
 * @returns {Promise<{success: boolean, error: Error|null, shareUrl: string|null}>}
 */
export const setThreadShared = async (threadId, isShared = true) => {
  try {
    // Get the current thread metadata
    const { data: thread, error: threadError } = await supabase
      .from("threads")
      .select("metadata")
      .eq("id", threadId)
      .single();

    if (threadError) {
      throw threadError;
    }

    // Update the thread metadata with shared status
    const updatedMetadata = {
      ...(thread.metadata || {}),
      is_shared_thread: isShared, // Keep for backward compatibility
      shared_at: isShared ? new Date().toISOString() : null,
    };

    // Update the thread in Supabase with new is_shared column
    const { error: updateError } = await supabase
      .from("threads")
      .update({
        is_shared: isShared,
        metadata: updatedMetadata,
        updated_at: new Date().toISOString(),
      })
      .eq("id", threadId);

    if (updateError) {
      throw updateError;
    }

    // Generate share URL
    const shareUrl = isShared
      ? `${window.location.origin}/share/chat/${threadId}`
      : null;

    return {
      success: true,
      error: null,
      shareUrl,
    };
  } catch (error) {
    console.error("Error setting thread shared status:", error);
    return {
      success: false,
      error,
      shareUrl: null,
    };
  }
};

/**
 * Check if a thread is publicly shared
 *
 * @param {string} threadId - The ID of the thread to check
 * @returns {Promise<{isShared: boolean, error: Error|null}>}
 */
export const isThreadShared = async (threadId) => {
  try {
    const { data: thread, error } = await supabase
      .from("threads")
      .select("is_shared, metadata")
      .eq("id", threadId)
      .single();

    if (error) {
      throw error;
    }

    // Check the new is_shared column first, fallback to metadata for backward compatibility
    const isShared =
      thread?.is_shared || Boolean(thread?.metadata?.is_shared_thread);

    return {
      isShared,
      error: null,
    };
  } catch (error) {
    console.error("Error checking thread shared status:", error);
    return {
      isShared: false,
      error,
    };
  }
};

/**
 * Get the share URL for a thread if it is shared
 *
 * @param {string} threadId - The ID of the thread
 * @returns {Promise<{shareUrl: string|null, error: Error|null}>}
 */
export const getThreadShareUrl = async (threadId) => {
  try {
    const { isShared, error } = await isThreadShared(threadId);

    if (error) {
      throw error;
    }

    const shareUrl = isShared
      ? `${window.location.origin}/share/chat/${threadId}`
      : null;

    return {
      shareUrl,
      error: null,
    };
  } catch (error) {
    console.error("Error getting thread share URL:", error);
    return {
      shareUrl: null,
      error,
    };
  }
};

/**
 * Set a canvas as publicly shareable
 *
 * @param {string} messageId - The ID of the message containing the canvas
 * @param {boolean} isShared - Whether the canvas should be shared (true) or private (false)
 * @returns {Promise<{success: boolean, error: Error|null, shareUrl: string|null}>}
 */
export const setCanvasShared = async (messageId, isShared = true) => {
  try {
    // Get the current message metadata
    const { data: message, error: messageError } = await supabase
      .from("thread_messages")
      .select("metadata, tool_calls")
      .eq("message_id", messageId)
      .single();

    if (messageError) {
      throw messageError;
    }

    // Update the message metadata with shared status
    const updatedMetadata = {
      ...(message.metadata || {}),
      is_shared_canvas: isShared,
      canvas_shared_at: isShared ? new Date().toISOString() : null,
    };

    // Update the message in Supabase
    const { error: updateError } = await supabase
      .from("thread_messages")
      .update({
        metadata: updatedMetadata,
      })
      .eq("message_id", messageId);

    if (updateError) {
      throw updateError;
    }

    // Generate share URL
    const shareUrl = isShared
      ? `${window.location.origin}/share/canvas/${messageId}`
      : null;

    return {
      success: true,
      error: null,
      shareUrl,
    };
  } catch (error) {
    console.error("Error setting canvas shared status:", error);
    return {
      success: false,
      error,
      shareUrl: null,
    };
  }
};

/**
 * Check if a canvas is publicly shared
 *
 * @param {string} messageId - The ID of the message containing the canvas
 * @returns {Promise<{isShared: boolean, error: Error|null}>}
 */
export const isCanvasShared = async (messageId) => {
  try {
    const { data: message, error } = await supabase
      .from("thread_messages")
      .select("metadata")
      .eq("message_id", messageId)
      .single();

    if (error) {
      throw error;
    }

    const isShared = Boolean(message?.metadata?.is_shared_canvas);

    return {
      isShared,
      error: null,
    };
  } catch (error) {
    console.error("Error checking canvas shared status:", error);
    return {
      isShared: false,
      error,
    };
  }
};

/**
 * Get the share URL for a canvas if it is shared
 *
 * @param {string} messageId - The ID of the message containing the canvas
 * @returns {Promise<{shareUrl: string|null, error: Error|null}>}
 */
export const getCanvasShareUrl = async (messageId) => {
  try {
    const { isShared, error } = await isCanvasShared(messageId);

    if (error) {
      throw error;
    }

    const shareUrl = isShared
      ? `${window.location.origin}/share/canvas/${messageId}`
      : null;

    return {
      shareUrl,
      error: null,
    };
  } catch (error) {
    console.error("Error getting canvas share URL:", error);
    return {
      shareUrl: null,
      error,
    };
  }
};

/**
 * Get shared canvas data for public viewing
 *
 * @param {string} messageId - The ID of the message containing the canvas
 * @returns {Promise<{canvasData: object|null, error: Error|null}>}
 */
export const getSharedCanvasData = async (messageId) => {
  try {
    // First check if the canvas is shared
    const { isShared, error: shareError } = await isCanvasShared(messageId);

    if (shareError) {
      throw shareError;
    }

    if (!isShared) {
      throw new Error("Canvas is not shared publicly");
    }

    // Get the message with canvas data
    // First, try to get the specific message
    const { data: message, error: messageError } = await supabase
      .from("thread_messages")
      .select(
        "tool_calls, metadata, created_at, thread_id, content, role, message_id"
      )
      .eq("message_id", messageId)
      .single();

    // If this message doesn't have tool_calls, it might be a user message
    // We need to find the corresponding assistant message with canvas tool calls
    if (message && (!message.tool_calls || message.role === "user")) {
      // Get the thread_id from the message and find assistant messages with canvas tool calls
      const { data: assistantMessages, error: assistantError } = await supabase
        .from("thread_messages")
        .select(
          "tool_calls, metadata, created_at, thread_id, content, role, message_id"
        )
        .eq("thread_id", message.thread_id)
        .eq("role", "assistant")
        .not("tool_calls", "is", null)
        .order("created_at", { ascending: false });

      if (
        !assistantError &&
        assistantMessages &&
        assistantMessages.length > 0
      ) {
        // Find the assistant message with canvas tool calls
        for (const assistantMsg of assistantMessages) {
          let toolCalls = assistantMsg.tool_calls;
          if (typeof toolCalls === "string") {
            try {
              toolCalls = JSON.parse(toolCalls);
            } catch (e) {
              continue;
            }
          }

          if (toolCalls && Array.isArray(toolCalls)) {
            const hasCanvasTool = toolCalls.some(
              (call) =>
                (call.function?.name === "canvas_create" ||
                  call.function?.name === "canvas_stream" ||
                  call.function?.name === "canvas_edit" ||
                  call.name === "canvas_create" ||
                  call.name === "canvas_stream" ||
                  call.name === "canvas_edit") &&
                call.result
            );

            if (hasCanvasTool) {
              // Use this assistant message instead
              Object.assign(message, assistantMsg);
              break;
            }
          }
        }
      }
    }

    if (messageError) {
      throw messageError;
    }

    // Extract canvas data from tool_calls - look for completed tool calls with results
    let canvasData = null;

    // Parse tool_calls if it's a JSON string
    let toolCalls = message.tool_calls;
    if (typeof toolCalls === "string") {
      try {
        toolCalls = JSON.parse(toolCalls);
      } catch (parseError) {
        toolCalls = null;
      }
    }

    if (toolCalls && Array.isArray(toolCalls)) {
      // Find canvas tool call with result (completed tool call)
      const canvasTool = toolCalls.find(
        (call) =>
          (call.function?.name === "canvas_create" ||
            call.function?.name === "canvas_stream" ||
            call.function?.name === "canvas_edit" ||
            call.name === "canvas_create" ||
            call.name === "canvas_stream" ||
            call.name === "canvas_edit") &&
          call.result
      );

      if (canvasTool && canvasTool.result) {
        // Extract canvas data from the result
        let resultData = canvasTool.result;

        // Handle nested canvas data structure
        if (resultData.canvasData) {
          resultData = resultData.canvasData;
        } else if (resultData.canvas) {
          resultData = resultData.canvas;
        }

        canvasData = {
          content: resultData.content || resultData.blocks || "",
          blocks: resultData.blocks || [],
          type: resultData.contentType || resultData.type || "text",
          language: resultData.language || "javascript",
          name: resultData.name || resultData.title || "Canvas",
          version: resultData.version || resultData.metadata?.version || 1,
          metadata: {
            ...resultData.metadata,
            created_at: message.created_at,
            shared_at: message.metadata?.canvas_shared_at,
            messageId: messageId,
            threadId: message.thread_id,
            fromSharedCanvas: true,
          },
        };
      } else {
        console.warn(
          "🎨 getSharedCanvasData: No canvas tool call with result found in tool_calls:",
          toolCalls
        );
      }
    } else {
      console.warn(
        "🎨 getSharedCanvasData: No valid tool_calls found in message. Raw tool_calls:",
        message.tool_calls,
        "Type:",
        typeof message.tool_calls
      );
    }

    if (!canvasData) {
      throw new Error("No canvas data found in message");
    }

    return {
      canvasData,
      error: null,
    };
  } catch (error) {
    console.error("Error getting shared canvas data:", error);
    return {
      canvasData: null,
      error,
    };
  }
};
